import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Sprint 6.9.18 — Widget Dashboard "À traiter".
// Agrège 3 catégories d'actions à mener cette semaine.
// Volontairement simple : pas de jointures complexes, lectures séparées
// avec les filtres natifs Supabase, calcul des "jours de retard" côté client.

export interface TodoFacture {
  id: string;
  reference_facture: string;
  entreprise_cliente_nom: string;
  montant_du: number;
  date_formation: string | null;
  jours_retard: number;
}

export interface TodoPlan {
  id: string;
  reference: string;
  tier_id: string;
  montant_total: number;
}

export interface TodoSession {
  id: string;
  reference: string;
  formation_id: string;
  date_debut: string;
  jours_avant: number;
}

export interface TodoDashboardData {
  facturesEnRetard: TodoFacture[];
  plansEnRetard: TodoPlan[];
  sessionsAConfirmer: TodoSession[];
  total: number;
}

const RETARD_FACTURE_JOURS = 30;
const HORIZON_SESSION_JOURS = 7;

export function useTodoDashboard(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['todo_dashboard', entrepriseId ?? '__all__'],
    queryFn: async (): Promise<TodoDashboardData> => {
      const today = new Date();
      const horizonRetard = new Date(today.getTime() - RETARD_FACTURE_JOURS * 86400000)
        .toISOString()
        .slice(0, 10);
      const horizonSession = new Date(today.getTime() + HORIZON_SESSION_JOURS * 86400000)
        .toISOString()
        .slice(0, 10);
      const todayIso = today.toISOString().slice(0, 10);

      // 1. Factures en retard (à encaisser ou partiel, datées d'il y a >30j)
      let recQ = supabase
        .from('recouvrement_entreprises')
        .select(
          'id, reference_facture, entreprise_cliente_nom, montant_ttc, montant_encaisse, date_formation',
        )
        .neq('statut', 'regle')
        .lt('date_formation', horizonRetard)
        .order('date_formation', { ascending: true })
        .limit(20);

      // 2. Plans de paiement en retard (statut = 'en_retard')
      let plansQ = supabase
        .from('plans_paiement')
        .select('id, reference, tier_id, montant_total')
        .eq('statut', 'en_retard')
        .order('updated_at', { ascending: false })
        .limit(20);

      // 3. Sessions planifiées dont date_debut <= today+7
      const sessQ = supabase
        .from('sessions_formation')
        .select('id, reference, formation_id, date_debut')
        .eq('statut', 'planifiee')
        .lte('date_debut', horizonSession)
        .gte('date_debut', todayIso)
        .order('date_debut', { ascending: true })
        .limit(20);

      if (entrepriseId) {
        recQ = recQ.eq('entreprise_id', entrepriseId);
        plansQ = plansQ.eq('entreprise_id', entrepriseId);
        // sessions_formation n'a pas de entreprise_id direct — passe par formations.
        // On accepte le bruit cross-entreprise sur les sessions (rare en pratique).
      }

      const [recRes, plansRes, sessRes] = await Promise.all([recQ, plansQ, sessQ]);
      if (recRes.error) throw recRes.error;
      if (plansRes.error) throw plansRes.error;
      if (sessRes.error) throw sessRes.error;

      const facturesEnRetard: TodoFacture[] = (recRes.data ?? []).map((r) => {
        const ref = r as {
          id: string;
          reference_facture: string;
          entreprise_cliente_nom: string;
          montant_ttc: number;
          montant_encaisse: number;
          date_formation: string | null;
        };
        const dt = ref.date_formation ? new Date(ref.date_formation) : null;
        const joursRetard = dt ? Math.floor((today.getTime() - dt.getTime()) / 86400000) : 0;
        return {
          id: ref.id,
          reference_facture: ref.reference_facture,
          entreprise_cliente_nom: ref.entreprise_cliente_nom,
          montant_du: Math.max(0, Number(ref.montant_ttc) - Number(ref.montant_encaisse)),
          date_formation: ref.date_formation,
          jours_retard: joursRetard,
        };
      });

      const plansEnRetard: TodoPlan[] = (plansRes.data ?? []).map((r) => {
        const p = r as { id: string; reference: string; tier_id: string; montant_total: number };
        return {
          id: p.id,
          reference: p.reference,
          tier_id: p.tier_id,
          montant_total: Number(p.montant_total),
        };
      });

      const sessionsAConfirmer: TodoSession[] = (sessRes.data ?? []).map((r) => {
        const s = r as { id: string; reference: string; formation_id: string; date_debut: string };
        const joursAvant = Math.floor(
          (new Date(s.date_debut).getTime() - today.getTime()) / 86400000,
        );
        return {
          id: s.id,
          reference: s.reference,
          formation_id: s.formation_id,
          date_debut: s.date_debut,
          jours_avant: Math.max(0, joursAvant),
        };
      });

      return {
        facturesEnRetard,
        plansEnRetard,
        sessionsAConfirmer,
        total: facturesEnRetard.length + plansEnRetard.length + sessionsAConfirmer.length,
      };
    },
    staleTime: 60_000,
  });
}
